import { useEffect, useMemo, useRef } from 'react';

import { useTeamData } from '@confluence/permissions-principals/entry-points/useTeamData.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import type { PublishModalRestrictionsInput } from './publishModalDataToQueriesState.ts';
import type { useQueriesShareAndRestrictDialogQuery as useQueriesShareAndRestrictDialogQuery$data } from './__types__/useQueriesShareAndRestrictDialogQuery.ts';
import { ConfluenceGroupUsageType } from './__types__/useQueriesShareAndRestrictDialogQuery.ts';
import type { useContentPermissionsQuery$data as ContentPermissionsData } from './__generated__/useContentPermissionsQuery.graphql.ts';

export type UseTeamDataResult = Omit<ReturnType<typeof useTeamData>, 'refetch'>;

export const useShareDialogTeamDataHandler = (
	cloudId: string,
	publishModalRestrictions?: PublishModalRestrictionsInput,
	data?: useQueriesShareAndRestrictDialogQuery$data,
	contentPermissionsData?: ContentPermissionsData,
	shouldUseNewQuery?: boolean,
): UseTeamDataResult => {
	const { teamData, isTeamDataLoading, teamDataError, refetch } = useTeamData(cloudId);

	const teamIds = useMemo(() => {
		const teamIdSet = new Set<string>();

		// Extract team IDs from publish modal restrictions
		// Using flatMap to flatten, then single forEach to add matching items
		const publishModalGroups = publishModalRestrictions?.flatMap(
			(item) => item?.restrictions?.group?.results ?? [],
		);
		publishModalGroups?.forEach((group) => {
			if (group?.id && group.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION) {
				teamIdSet.add(group.id);
			}
		});

		// New query path
		const contentPermissionNodes =
			shouldUseNewQuery &&
			contentPermissionsData?.confluence_contentPermissions?.confluencePrincipalsConnection?.nodes;
		if (contentPermissionNodes) {
			contentPermissionNodes.forEach((node) => {
				if (
					node?.principalId &&
					node.principalType === 'GROUP' &&
					node.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION
				) {
					teamIdSet.add(node.principalId);
				}
			});
			return Array.from(teamIdSet);
		}

		// Legacy path (share dialog)
		if (data?.content?.nodes?.[0]?.restrictions) {
			const readNodes = data.content.nodes[0].restrictions.read?.restrictions?.group?.nodes ?? [];
			const updateNodes =
				data.content.nodes[0].restrictions.update?.restrictions?.group?.nodes ?? [];
			const allNodes = [...readNodes, ...updateNodes];
			allNodes.forEach((node) => {
				if (node?.id && node?.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION) {
					teamIdSet.add(node.id);
				}
			});
		}

		return Array.from(teamIdSet);
	}, [publishModalRestrictions, data, contentPermissionsData, shouldUseNewQuery]);

	// Track previous teamIds to avoid unnecessary refetches
	const prevTeamIdsRef = useRef<string[]>([]);
	// Track whether we've completed the initial team data fetch for discovered team IDs
	const hasCompletedTeamDataFetch = useRef(false);
	// Track whether a refetch is in progress
	const isRefetchingRef = useRef(false);

	// Effect to trigger team data refetch when team IDs change
	useEffect(() => {
		// Only refetch if teamIds have changed
		const teamIdsChanged =
			teamIds.length !== prevTeamIdsRef.current.length ||
			teamIds.some((id, i) => id !== prevTeamIdsRef.current[i]);

		if (teamIds.length > 0 && teamIdsChanged) {
			prevTeamIdsRef.current = teamIds;
			hasCompletedTeamDataFetch.current = false;
			isRefetchingRef.current = true;
			// Convert team IDs to ARI format for AGG query
			const ariTeamIds = teamIds.map((id) => `ari:cloud:identity::team/${id}`);
			void refetch({
				teamIds: ariTeamIds,
				siteId: cloudId,
			});
		}
	}, [teamIds, cloudId, refetch]);

	// Effect to update refs when loading completes
	// This avoids mutating refs during render, which can cause issues with React's concurrent features
	useEffect(() => {
		if (!isTeamDataLoading && isRefetchingRef.current) {
			hasCompletedTeamDataFetch.current = true;
			isRefetchingRef.current = false;
		}
	}, [isTeamDataLoading]);

	// Determine if we need to fetch team data (team IDs exist but haven't been fetched yet)
	const needsTeamDataFetch = teamIds.length > 0 && !hasCompletedTeamDataFetch.current;

	// The effective loading state:
	// - isTeamDataLoading: true when useTeamData query is actively loading
	// - isRefetchingRef.current: true when we've triggered a refetch and are waiting for it to complete
	// - needsTeamDataFetch: true when team IDs exist but we haven't started/completed fetching yet
	const effectiveIsTeamDataLoading =
		expValEquals('persistence_specific_access_gate', 'cohort', 'experiment') ||
		fg('confluence_frontend_perms_exp_teams_as_principals')
			? isTeamDataLoading || isRefetchingRef.current || needsTeamDataFetch
			: isTeamDataLoading;

	return {
		teamData,
		isTeamDataLoading: effectiveIsTeamDataLoading,
		teamDataError,
	};
};
